import { Modal } from './modal.js';

class AgencyFinder {
	constructor( modal ) {
		this.modal = modal;
		this.modalElement = modal.modal;
		this.input = this.modalElement.querySelector( '.agency-finder__input' );
		this.button = this.modalElement.querySelector( '.agency-finder__button' );
		this.inputWrap = this.modalElement.querySelector( '.agency-finder__input-wrap' );
		this.agencyFinder = this.modalElement.querySelector( '.agency-finder' );
		this.resultsContainer = null;

		if ( ! this.input || ! this.button ) {
			return;
		}

		// Use relative path for WordPress REST API
		this.apiBaseUrl = '/wp-json/people-of-hope/v1/agency-finder';
		this.isLoading = false;
		this.i18n = this.readI18n();

		this.init();
	}

	readI18n() {
		const defaults = {
			error_invalid_zip: 'Please enter a valid 5-digit zip code.',
			error_fetch: "Sorry, we couldn't find any agencies. Please try again.",
			no_results: 'No agencies found.',
			learn_more: 'Learn More',
			agency_alt: 'Agency',
		};
		const container = this.modalElement.querySelector( '.agency-finder__i18n' );
		if ( ! container ) {
			return defaults;
		}
		container.querySelectorAll( '[data-i18n]' ).forEach( ( el ) => {
			const key = el.dataset.i18n;
			const text = el.textContent.trim();
			if ( key in defaults && text ) {
				defaults[ key ] = text;
			}
		} );
		return defaults;
	}

	init() {
		// Restrict input to numbers only
		this.input.addEventListener( 'input', ( e ) => {
			// Remove any non-numeric characters
			e.target.value = e.target.value.replace( /\D/g, '' );
		} );

		// Handle button click
		this.button.addEventListener( 'click', ( e ) => {
			e.preventDefault();
			this.search();
		} );

		// Handle Enter key in input
		this.input.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Enter' ) {
				e.preventDefault();
				this.search();
			}
		} );

		// Clear results when modal opens
		this.modal.modal.addEventListener( 'modal:open', () => {
			this.clearResults();
			if ( this.input ) {
				this.input.value = '';
				this.input.focus();
			}
		} );
	}

	async search() {
		const zipCode = this.input.value.trim();

		if ( ! zipCode ) {
			return;
		}

		// Validate zip code (basic validation - 5 digits)
		if ( ! /^\d{5}$/.test( zipCode ) ) {
			this.showError( this.i18n.error_invalid_zip );
			return;
		}

		this.setLoading( true );
		this.clearResults();

		try {
			const lang = ( document.documentElement.lang || 'en' ).split( '-' )[ 0 ].toLowerCase();
			const url = `${ this.apiBaseUrl }?zip_code=${ encodeURIComponent( zipCode ) }&lang=${ encodeURIComponent( lang ) }`;
			const response = await fetch( url );

			if ( ! response.ok ) {
				throw new Error( `HTTP error! status: ${ response.status }` );
			}

			const data = await response.json();
			this.displayResults( data );
		} catch ( error ) {
			console.error( 'Agency finder error:', error );
			this.showError( this.i18n.error_fetch );
		} finally {
			this.setLoading( false );
		}
	}

	setLoading( loading ) {
		this.isLoading = loading;

		if ( loading ) {
			this.inputWrap.classList.add( 'is-loading' );
			this.button.disabled = true;
		} else {
			this.inputWrap.classList.remove( 'is-loading' );
			this.button.disabled = false;
		}
	}

	ensureResultsContainer() {
		if ( ! this.resultsContainer ) {
			this.resultsContainer = document.createElement( 'div' );
			this.resultsContainer.className = 'agency-finder__results';
			this.agencyFinder.appendChild( this.resultsContainer );
		}
		return this.resultsContainer;
	}

	buildAddressHTML( agency ) {
		if ( agency.formatted_address ) {
			return `<p class="agency-finder__result-address">${ this.escapeHtml( agency.formatted_address ) }</p>`;
		}

		let addressHTML = '';
		const addressParts = [];

		if ( agency.address ) {
			addressParts.push( agency.address );
		}
		if ( agency.apartment_or_suite ) {
			addressParts.push( agency.apartment_or_suite );
		}
		if ( addressParts.length > 0 ) {
			addressHTML = `<p class="agency-finder__result-address">${ this.escapeHtml( addressParts.join( ', ' ) ) }</p>`;
		}

		if ( agency.city && agency.state ) {
			const locationParts = [
				agency.city,
				agency.state,
			];
			if ( agency.zip_code ) {
				locationParts.push( agency.zip_code );
			}
			addressHTML += `<p class="agency-finder__result-location">${ this.escapeHtml( locationParts.join( ', ' ) ) }</p>`;
		}

		return addressHTML;
	}

	buildImageHTML( agency ) {
		if ( ! agency.featured_image ) {
			return '';
		}

		const altText = this.escapeHtml( agency.title || agency.name || this.i18n.agency_alt );
		return `<div class="agency-finder__result-image"><img src="${ this.escapeHtml( agency.featured_image ) }" alt="${ altText }"></div>`;
	}

	buildLinkHTML( agency ) {
		const linkUrl = agency.website_url
			? agency.website_url.startsWith( 'http' )
				? agency.website_url
				: `https://${ agency.website_url }`
			: agency.link || null;

		if ( ! linkUrl ) {
			return '';
		}

		return `<div class="wp-block-buttons"><a class="wp-block-button__link" href="${ this.escapeHtml(
			linkUrl
		) }" target="_blank" rel="noopener">${ this.escapeHtml( this.i18n.learn_more ) }</a></div>`;
	}

	buildAgencyCard( agency ) {
		const title = this.escapeHtml( agency.title || agency.name || this.i18n.agency_alt );
		const addressHTML = this.buildAddressHTML( agency );
		const imageHTML = this.buildImageHTML( agency );
		const linkHTML = this.buildLinkHTML( agency );
		const excerptHTML = agency.excerpt ? `<p class="agency-finder__result-excerpt">${ this.escapeHtml( agency.excerpt ) }</p>` : '';

		return `
			<div class="agency-finder__result">
				${ imageHTML }
				<div class="agency-finder__result-content">
					<h3 class="agency-finder__result-name has-h5-variant">${ title }</h3>
					${ excerptHTML }
					${ linkHTML }
				</div>
			</div>
		`;
	}

	displayResults( data ) {
		const container = this.ensureResultsContainer();
		container.innerHTML = '';

		if ( ! data || ( Array.isArray( data ) && data.length === 0 ) ) {
			container.innerHTML = `
				<div class="agency-finder__no-results">
					<p>${ this.escapeHtml( this.i18n.no_results ) }</p>
				</div>
			`;
			return;
		}

		const results = Array.isArray( data ) ? data : [ data ];
		const resultsHTML = results.map( ( agency ) => this.buildAgencyCard( agency ) ).join( '' );

		container.innerHTML = `
			<div class="agency-finder__results-list">
				${ resultsHTML }
			</div>
		`;

		// Trigger animation by forcing a reflow, then adding the class
		const resultsList = container.querySelector( '.agency-finder__results-list' );
		if ( resultsList ) {
			// Use requestAnimationFrame to ensure initial state is rendered first
			requestAnimationFrame( () => {
				// Force reflow to ensure initial state is applied
				void resultsList.offsetHeight;
				// Add class to trigger animations
				resultsList.classList.add( 'is-visible' );
			} );
		}

		setTimeout( () => {
			container.scrollIntoView( { behavior: 'smooth', block: 'nearest' } );
		}, 100 );
	}

	showError( message ) {
		const container = this.ensureResultsContainer();
		container.innerHTML = `
			<div class="agency-finder__error">
				<p>${ this.escapeHtml( message ) }</p>
			</div>
		`;
	}

	clearResults() {
		if ( this.resultsContainer ) {
			this.resultsContainer.innerHTML = '';
		}
	}

	escapeHtml( text ) {
		const div = document.createElement( 'div' );
		div.textContent = text;
		return div.innerHTML;
	}
}

export function initAgencyFinderModal() {
	const modal = new Modal( '.agency-finder-toggle, a[href="#agency-finder-modal"]', {
		modalId: 'agency-finder-modal',
	} );

	if ( modal && modal.modal ) {
		new AgencyFinder( modal );
	}

	return modal;
}

document.addEventListener( 'DOMContentLoaded', initAgencyFinderModal );
